import type { EstimatedDuration } from '@axiom/const';
import type { Location } from '@axiom/types';
import moment from 'moment';
import type {
  Account,
  Language,
  ProjectNeedsWithLegalSkills,
  Taxonomy,
} from '@axiom/validation';
import {
  estimatedDurationValues,
  levelOfExperienceValues,
  PositionsConst,
  selfServiceCountryCodes,
} from '@axiom/const';
import { TaxonomyUtil } from '@axiom/ui';

export interface PositionDescriptionSection {
  name: string;
  type: 'paragraph' | 'bulletList';
  content: string | string[];
  header?: string;
}

export interface EngagementDescriptionInput {
  addressCountryCode: keyof typeof selfServiceCountryCodes | null;
  billingUnitsPerWeek?: number | null;
  startDate?: string | null;
  estimatedDuration?: string | null;
  worksite?: string | null;
  companyLocation?: Location | null;
}

type AddressComponent = NonNullable<
  NonNullable<Location['locationAddressComponents']>['addresses']
>[number];

export const EngagementUtil = {
  calculateEndDateFromEstimatedDuration(
    startDate: string | undefined,
    estimatedDuration: EstimatedDuration | undefined
  ): string | null {
    if (!startDate || !estimatedDuration) {
      return null;
    }

    const durationMap: Record<EstimatedDuration, number> = {
      ONE_MONTH: 1,
      TWO_MONTHS: 2,
      THREE_MONTHS: 3,
      FOUR_MONTHS: 4,
      FIVE_MONTHS: 5,
      SIX_MONTHS: 6,
      SEVEN_MONTHS: 7,
      EIGHT_MONTHS: 8,
      NINE_MONTHS: 9,
      TEN_MONTHS: 10,
      ELEVEN_MONTHS: 11,
      TWELVE_MONTHS_PLUS: 12,
    };

    const monthsToAdd = durationMap[estimatedDuration];

    const endDate = moment(startDate).add(monthsToAdd, 'months');
    return endDate.isValid() ? endDate.format('YYYY-MM-DD') : null;
  },

  findLocality(
    addressComponents: AddressComponent[],
    types: string[]
  ): string | undefined {
    for (const type of types) {
      const component = addressComponents.find(address =>
        address.types.includes(type)
      );
      if (component?.long_name) return component.long_name;
    }
    return undefined;
  },

  findState(locationData?: Location): string | null {
    const addresses = locationData?.locationAddressComponents?.addresses;
    if (!Array.isArray(addresses)) return null;
    for (const address of addresses) {
      if (address.types.includes('administrative_area_level_1')) {
        return address.long_name;
      }
    }
    return null;
  },

  extractAddressFields(companyLocation?: Location | null): {
    addressStreet: string | undefined;
    addressCity: string | undefined;
    addressState: string | undefined;
    addressZip: string | undefined;
  } {
    let addressStreet = '';
    let addressCity: string | undefined;
    let addressState: string | undefined;
    let addressZip: string | undefined;

    companyLocation?.locationAddressComponents?.addresses?.forEach(line => {
      if (line.types.includes('street_number')) {
        addressStreet += `${line.long_name} `;
      }
      if (line.types.includes('route')) {
        addressStreet += line.long_name;
      }
      if (line.types.includes('sublocality_level_1')) {
        addressCity = line.long_name;
      }
      if (line.types.includes('locality')) {
        addressCity = line.long_name;
      }
      if (line.types.includes('administrative_area_level_1')) {
        addressState = line.long_name;
      }
      if (line.types.includes('postal_code')) {
        addressZip = line.long_name;
      }
    });

    return {
      addressStreet: addressStreet || undefined,
      addressCity,
      addressState,
      addressZip,
    };
  },

  serializePositionDescription(sections: PositionDescriptionSection[]): string {
    return sections
      .map(section => {
        if (section.type === 'paragraph') {
          return section.content;
        }
        const bullets = (section.content as string[])
          .map(bullet => `• ${bullet}`)
          .join('\n');
        return section.header ? `${section.header}\n${bullets}` : bullets;
      })
      .join('\n\n');
  },

  buildOtherFocusAreasDescription(
    values: Partial<ProjectNeedsWithLegalSkills>,
    taxonomy: Taxonomy
  ): string | null {
    if (!values.otherFocusAreas || values.otherFocusAreas.length === 0) {
      return null;
    }

    return values.otherFocusAreas
      .map(faId => taxonomy.focusAreas[faId]?.standaloneDisplayName)
      .map((name, idx, arr) => {
        if (arr.length > 1) {
          if (idx === arr.length - 1) {
            return `and ${name}`;
          } else if (idx === arr.length - 2) {
            return `${name}`;
          }
          return `${name},`;
        }
        return name;
      })
      .join(' ');
  },

  buildFocusAreaDescription(
    values: Partial<ProjectNeedsWithLegalSkills>,
    accountName: string,
    taxonomy: Taxonomy
  ): PositionDescriptionSection | null {
    const primaryFocusArea = values.primaryFocusArea
      ? taxonomy.focusAreas[values.primaryFocusArea]?.standaloneDisplayName
      : null;

    if (!primaryFocusArea) return null;

    let paragraph = `${accountName} is looking for an Axiom legal pro with a background in ${primaryFocusArea}`;

    const otherFocusAreasText = EngagementUtil.buildOtherFocusAreasDescription(
      values,
      taxonomy
    );
    if (otherFocusAreasText) {
      paragraph += ` and additional experience in ${otherFocusAreasText}`;
    }

    const industry = values.industry
      ? taxonomy.industries[values.industry]?.displayValue
      : null;
    if (industry) {
      paragraph += `. Industry experience in ${industry} is essential`;
    }

    const experience = values.levelOfExperience
      ? levelOfExperienceValues[values.levelOfExperience]
      : null;
    if (experience) {
      paragraph += `. ${experience} of legal experience is preferred`;
    }

    return {
      name: 'FOCUS_AREA_DESCRIPTION',
      type: 'paragraph',
      content: `${paragraph}.`,
    };
  },

  buildLegalSkillsBullets(
    values: Partial<ProjectNeedsWithLegalSkills>,
    taxonomy: Taxonomy
  ): string[] {
    const bullets: string[] = [];

    const skillGroups = values.primaryFocusArea
      ? TaxonomyUtil.getFocusAreaSkillGroups(
          values.primaryFocusArea,
          taxonomy.skillGroups
        )
      : [];

    if (!values.legalSkillsAnswers || values.legalSkillsAnswers.length === 0) {
      return bullets;
    }

    values.legalSkillsAnswers.forEach((answers, index) => {
      if (!answers || answers.length === 0) return;
      const skillGroup = skillGroups[index];
      if (!skillGroup) return;

      const skillNames = answers
        .map(skillId => taxonomy.legalSkills[skillId]?.fullName)
        .join(', ');

      if (skillNames) {
        bullets.push(`${skillGroup.fullName}: ${skillNames}`);
      }
    });

    return bullets;
  },

  buildSkillsDescription(
    values: Partial<ProjectNeedsWithLegalSkills>,
    taxonomy: Taxonomy,
    languages: Language[]
  ): PositionDescriptionSection | null {
    const skillsBullets = [];

    skillsBullets.push(
      ...EngagementUtil.buildLegalSkillsBullets(values, taxonomy)
    );

    const techSkills = values.legalTechSkills
      ?.map(skillId => taxonomy.legalTechSkills[skillId]?.displayName)
      .join(', ');
    if (techSkills) {
      skillsBullets.push(`Legal tech skills: ${techSkills}`);
    }

    const generalSkills = values.generalSkills
      ?.map(skillId => taxonomy.generalSkills[skillId]?.displayName)
      .join(', ');
    if (generalSkills) {
      skillsBullets.push(`Additional relevant skills: ${generalSkills}`);
    }

    const language = languages.find(lang => lang.id === values.languageId);
    if (language) {
      skillsBullets.push(`Business proficiency in: ${language.name}`);
    }

    if (skillsBullets.length === 0) return null;

    return {
      name: 'SKILLS_DESCRIPTION',
      type: 'bulletList',
      header: 'This work requires the following skills and proficiencies:',
      content: skillsBullets,
    };
  },

  buildLocationDescription(companyLocation?: Location | null): string | null {
    const addressComponents =
      companyLocation?.locationAddressComponents?.addresses;

    if (!addressComponents) return null;

    const city = EngagementUtil.findLocality(addressComponents, [
      'locality',
      'sublocality_level_1',
    ]);

    const state = EngagementUtil.findLocality(addressComponents, [
      'administrative_area_level_1',
    ]);
    const country = EngagementUtil.findLocality(addressComponents, ['country']);

    const locationParts = [city, state, country].filter(Boolean);

    return locationParts.length > 0 ? locationParts.join(', ') : null;
  },

  buildEngagementDescription(
    values: EngagementDescriptionInput | Partial<ProjectNeedsWithLegalSkills>
  ): PositionDescriptionSection | null {
    if (
      !values.billingUnitsPerWeek ||
      !values.startDate ||
      !values.estimatedDuration
    ) {
      return null;
    }

    const startDate = moment(values.startDate).format('MMMM D, YYYY');
    const duration =
      estimatedDurationValues[values.estimatedDuration as EstimatedDuration];

    let paragraph = `This is a ${values.billingUnitsPerWeek}-hour per week engagement, expected to begin on ${startDate} for ${duration} (subject to adjustment based on business needs)`;

    const worksiteMap: Record<string, string> = {
      Remote: 'remote',
      Onsite: 'on-site',
      'Onsite/Remote': 'hybrid remote and on-site',
    };

    const worksiteText = values.worksite ? worksiteMap[values.worksite] : null;

    if (worksiteText) {
      paragraph += `. This role will be ${worksiteText}`;

      if (
        values.worksite === PositionsConst.Worksites.Remote &&
        values.addressCountryCode
      ) {
        paragraph += ` (${selfServiceCountryCodes[values.addressCountryCode]})`;
      }

      const shouldShowLocation = [
        PositionsConst.Worksites.Onsite,
        PositionsConst.Worksites.OnsiteRemote,
      ].includes(values.worksite);

      if (shouldShowLocation) {
        const locationText = EngagementUtil.buildLocationDescription(
          values.companyLocation
        );
        if (locationText) {
          paragraph += ` and based in ${locationText}`;
        }
      }
    }

    return {
      name: 'ENGAGEMENT_DESCRIPTION',
      type: 'paragraph',
      content: `${paragraph}.`,
    };
  },

  buildAdditionalDetailsDescription(
    values: Partial<ProjectNeedsWithLegalSkills>,
    accountName: string
  ): PositionDescriptionSection | null {
    if (!values.additionalDetailsForTalent?.trim()) {
      return null;
    }

    return {
      name: 'ADDITIONAL_DESCRIPTION',
      type: 'paragraph',
      content: `${accountName} has provided the following context for this engagement: "${values.additionalDetailsForTalent}".`,
    };
  },

  buildPositionDescription({
    values,
    accountName,
    taxonomy,
    languages,
  }: {
    values: Partial<ProjectNeedsWithLegalSkills>;
    accountName: Account['calculatedName'];
    taxonomy: Taxonomy;
    languages: Language[];
  }): PositionDescriptionSection[] {
    if (!accountName) {
      return [];
    }

    const sections: PositionDescriptionSection[] = [];

    const primaryFocusSection = EngagementUtil.buildFocusAreaDescription(
      values,
      accountName,
      taxonomy
    );

    if (primaryFocusSection) {
      sections.push(primaryFocusSection);
    }

    const skillsSection = EngagementUtil.buildSkillsDescription(
      values,
      taxonomy,
      languages
    );

    if (skillsSection) {
      sections.push(skillsSection);
    }

    const engagementSection = EngagementUtil.buildEngagementDescription(values);

    if (engagementSection) {
      sections.push(engagementSection);
    }

    const additionalDetailsSection =
      EngagementUtil.buildAdditionalDetailsDescription(values, accountName);

    if (additionalDetailsSection) {
      sections.push(additionalDetailsSection);
    }

    return sections;
  },

  buildMiniIntakePositionDescription(
    values: EngagementDescriptionInput,
    accountName: string,
    primaryFocusAreaName?: string
  ): string {
    const sections: PositionDescriptionSection[] = [];

    if (primaryFocusAreaName) {
      sections.push({
        name: 'FOCUS_AREA_DESCRIPTION',
        type: 'paragraph',
        content: `${accountName} is looking for an Axiom legal pro with a background in ${primaryFocusAreaName}.`,
      });
    }

    const engagementSection = EngagementUtil.buildEngagementDescription(values);
    if (engagementSection) {
      sections.push(engagementSection);
    }

    return EngagementUtil.serializePositionDescription(sections);
  },
};
